import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatShort } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const VIEWS = [["all", "Everything", {}], ["nothing", "Found nothing", { nothing_only: true }],
  ["typed", "Typed only", { kind: "typed" }], ["scan", "Scans only", { kind: "scan" }]];
const PAGE = 50;

function Row({ r }) {
  const filters = Object.keys(r.filters || {});
  return (
    <tr data-testid={`sr-row-${r.id}`} className="border-t border-slate-100 align-top">
      <td className="px-3 py-2.5 whitespace-nowrap text-slate-500">{formatShort(r.at)}</td>
      <td className="px-3 py-2.5 font-medium text-slate-900 break-all">{r.kind === "scan" ? <span className="font-mono">{r.words}</span> : r.words}</td>
      <td className="px-3 py-2.5 text-slate-600">{r.box}</td>
      <td className={`px-3 py-2.5 tabular-nums font-mono ${r.found ? "text-slate-700" : "text-[#B01E28] font-bold"}`}>{r.found}{r.found_is_lower_bound ? "+" : ""}</td>
      <td className="px-3 py-2.5 text-slate-600">{r.role}</td>
      <td className="px-3 py-2.5 text-slate-500">{r.opened_rank ? `#${r.opened_rank}` : "—"}</td>
      <td className="px-3 py-2.5 text-slate-400 text-xs">{r.why === "archived" ? "archived place" : filters.length ? filters.join(", ") : ""}</td>
    </tr>
  );
}

export function SearchRecordAll() {
  const [view, setView] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = { page, page_size: PAGE, ...VIEWS.find((v) => v[0] === view)[2] };
    api.get("/admin/search-record/recent", { params }).then(({ data: d }) => setData(d)).catch(() => setData({ items: [], total: 0 }));
  }, [view, page]);
  return (
    <div className="space-y-4" data-testid="sr-all">
      <div className="flex flex-wrap gap-2">
        {VIEWS.map(([k, label]) => (
          <button key={k} type="button" data-testid={`sr-all-view-${k}`} onClick={() => { setView(k); setPage(1); }}
            className={`rounded-full h-10 px-4 text-sm font-medium border transition-colors ${view === k ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{label}</button>
        ))}
      </div>
      {!data ? <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
        <>
          <div data-testid="sr-all-count" className="text-sm text-slate-500"><b className="text-slate-900">{data.total}</b> on the record · removed after {data.keep_days || 90} days</div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-400">
                <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Words / code</th><th className="text-left px-3 py-2">Box</th>
                  <th className="text-left px-3 py-2">Found</th><th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Opened</th><th className="text-left px-3 py-2">Note</th></tr>
              </thead>
              <tbody>{data.items.map((r) => <Row key={r.id} r={r} />)}</tbody>
            </table>
            {!data.items.length && <div data-testid="sr-all-empty" className="p-6 text-center text-sm text-slate-400">Nothing on the record yet.</div>}
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="sr-all-prev" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-11 rounded-lg">Newer</Button>
            <span className="text-xs text-slate-500">Page {page}</span>
            <Button data-testid="sr-all-next" variant="outline" disabled={!data.has_more} onClick={() => setPage((p) => p + 1)} className="h-11 rounded-lg">Older</Button>
          </div>
        </>
      )}
    </div>
  );
}
